import { useMemo } from 'react';

import { Divider, FlexAligner, Row } from '@scaleaq/scaleui';
import { CameraInfo, InventoryHelper, InventoryProvider } from '@scaleaq/sitecontrol-lib';

import { colors, getValueByProvider, responsive, round, translateUnitLabel } from '@/utils';

import { StyledSensorValue } from './cameraSensorReading.styles.ts';
import { useCameraChanged } from './useCameraChanged.ts';

const cameraProviderToShow = ['temperature', 'o2percentage', 'depth'];

export const CameraSensorReading = ({ camera }: { camera: CameraInfo }) => {
  const cameraData = useCameraChanged(camera.id);

  const cameraProviderValues = useMemo(() => {
    if (!cameraData) return;
    const providerValues: Partial<Record<InventoryProvider, number | boolean | undefined>> = {};
    const envData: InventoryProvider[] =
      camera?.privateProviders?.filter((provider: string) =>
        cameraProviderToShow.includes(provider.toLowerCase())
      ) ?? [];
    envData?.forEach(provider => {
      providerValues[provider] = getValueByProvider(cameraData, provider);
    });
    return providerValues;
  }, [camera?.privateProviders, cameraData]);
  if (!cameraProviderValues) return null;

  const filteredValues = Object.entries(cameraProviderValues).filter(
    ([, v]) => v !== null && v !== undefined
  );
  if (filteredValues.length === 0) return null;

  const formattedValues = filteredValues.map(([provider, value]) => {
    return [provider, round(value as number, 1)] as [string, number];
  });

  return (
    <FlexAligner
      direction={'row'}
      alignment={'center-right'}
      gap={responsive.spacing.large}
      fullWidth={false}
      scaling={2}
    >
      {formattedValues.map(([provider, value], index) => (
        <Row key={provider} gap={responsive.spacing.large}>
          <StyledSensorValue type={'body_large'}>
            {`${value} ${translateUnitLabel(InventoryHelper.inventoryProviderUnitPair[provider])}`}
          </StyledSensorValue>
          {index < formattedValues.length - 1 && (
            <Divider orientation='vertical' color={colors.stroke.neutral.solid[100]} />
          )}
        </Row>
      ))}
    </FlexAligner>
  );
};
